import { ExternalLink } from "lucide-react";
import { ApiError, API_ORIGIN } from "../../lib/api";
import { Skeleton } from "../../components/ui/Feedback";
import { Select } from "../../components/ui/Input";
import { Switch } from "../../components/ui/Switch";
import { LabelWithHint } from "../../components/ui/Tooltip";
import type { StoreTheme, ThemeVisibility } from "../../lib/types";
import { useSession } from "../auth/useSession";
import { useThemes } from "./useThemes";

const VISIBILITY_HELP =
	"Which of your themes this campaign appears on. Preview is for testing on an unpublished theme: shoppers on the live theme see nothing, and no discount is created, so nothing can be redeemed.";

const PREVIEW_WARNING =
	"While previewing, no discount is registered with Shopify. Rewards show in the cart but are not taken off at checkout. Switch to all themes or the live theme and publish to make them real.";

const roleLabel = (theme: StoreTheme): string =>
	theme.role === "main" ? "Live" : theme.role === "unpublished" ? "Unpublished" : theme.role;

/**
 * Shopify opens any theme on the storefront from its numeric id. An unpublished
 * theme only previews for someone signed in to the store's admin, which is who
 * is looking at this page.
 */
const previewUrl = (shopDomain: string, themeId: string): string =>
	`https://${shopDomain}/?preview_theme_id=${encodeURIComponent(themeId.split("/").pop() ?? themeId)}`;

const ThemeRowSkeleton = () => (
	<div className="flex items-start gap-3">
		<Skeleton className="mt-0.5 h-5 w-9 rounded-full" />
		<div className="space-y-1.5 pt-0.5">
			<Skeleton className="h-3 w-40" />
			<Skeleton className="h-2.5 w-16" />
		</div>
	</div>
);

/**
 * Reading themes needs a scope the earliest installs never granted, so the
 * failure a merchant is most likely to hit is one they can fix themselves.
 */
const ThemesUnavailable = ({ error }: { error: Error }) => {
	const session = useSession();
	const needsReconnect = error instanceof ApiError && error.status === 403;
	const shop = session.data?.tenant.shopDomain;

	if (!needsReconnect || !shop) {
		return (
			<div className="rounded-md border border-line-strong bg-raised px-3 py-2 text-xs text-ink-muted">
				<p className="text-ink">Could not load your themes.</p>
				<p className="mt-1">{error.message}</p>
			</div>
		);
	}

	return (
		<div className="space-y-2 rounded-md border border-line-strong bg-raised px-3 py-2 text-xs text-ink-muted">
			<p className="text-ink">AOVtools cannot read your themes yet.</p>
			<p>
				Theme access was added after you installed. Reconnecting takes a moment and changes nothing
				else about the app.
			</p>
			<a
				className="inline-block font-medium text-ink underline underline-offset-4"
				href={`${API_ORIGIN}/shopify-auth?shop=${encodeURIComponent(shop)}`}
			>
				Reconnect store
			</a>
		</div>
	);
};

type ThemeVisibilityFieldProps = {
	visibility: ThemeVisibility;
	themeIds: string[];
	onChange: (next: { themeVisibility: ThemeVisibility; themeIds: string[] }) => void;
};

export const ThemeVisibilityField = ({
	visibility,
	themeIds,
	onChange,
}: ThemeVisibilityFieldProps) => {
	const themes = useThemes();
	const session = useSession();
	const shopDomain = session.data?.tenant.shopDomain;
	const items = themes.data?.items ?? [];

	const toggle = (themeId: string, checked: boolean) =>
		onChange({
			themeVisibility: visibility,
			themeIds: checked ? [...themeIds, themeId] : themeIds.filter((id) => id !== themeId),
		});

	return (
		<div className="max-w-md space-y-3">
			<LabelWithHint label="Themes">{VISIBILITY_HELP}</LabelWithHint>
			<Select
				value={visibility}
				onChange={(event) =>
					onChange({
						themeVisibility: event.target.value as ThemeVisibility,
						themeIds: event.target.value === "preview" ? themeIds : [],
					})
				}
			>
				<option value="all_themes">Every theme</option>
				<option value="live_theme">Live theme only</option>
				<option value="preview">Preview on specific themes</option>
			</Select>

			{visibility === "preview" ? (
				<div className="space-y-3">
					<p className="rounded-md border border-line-strong bg-raised px-3 py-2 text-xs text-ink-muted">
						{PREVIEW_WARNING}
					</p>

					{themes.isPending ? (
						<div className="space-y-3" aria-busy="true" aria-label="Loading themes">
							<ThemeRowSkeleton />
							<ThemeRowSkeleton />
							<ThemeRowSkeleton />
						</div>
					) : null}

					{themes.isError ? <ThemesUnavailable error={themes.error} /> : null}

					{items.map((theme) => (
						<div key={theme.id} className="flex items-start justify-between gap-3">
							<Switch
								checked={themeIds.includes(theme.id)}
								onChange={(event) => toggle(theme.id, event.target.checked)}
								label={theme.name}
								hint={roleLabel(theme)}
							/>
							{shopDomain ? (
								<a
									href={previewUrl(shopDomain, theme.id)}
									target="_blank"
									rel="noopener noreferrer"
									className="inline-flex shrink-0 items-center gap-1 pt-0.5 text-xs text-ink-muted underline-offset-4 hover:text-ink hover:underline"
									aria-label={`Preview ${theme.name} in a new tab`}
								>
									Preview
									<ExternalLink className="h-3 w-3" strokeWidth={2} />
								</a>
							) : null}
						</div>
					))}

					{themes.data && themeIds.length === 0 ? (
						<p className="text-xs text-ink-muted">
							Pick at least one theme, or the campaign appears nowhere.
						</p>
					) : null}
				</div>
			) : null}
		</div>
	);
};
